/**
 * @file    FieldsManagerHeader.tsx
 * @scope   VIEW
 * @summary Header of the Add/Remove Fields screen: data-source name, field/filter counts, the
 *          add-field / expand-all / search affordances and invalid-state indicators.
 * @consumes appAssets icons, OptionCheckbox, DataValidationTip
 * @produces `FieldsManagerHeader` component
 * @sideEffects None — presentational; actions bubble up via callbacks.
 * @notes Switches from the expand-all toggle to a search box once the field count hits the
 *        search threshold (see FieldsManager/useFieldsFilter).
 */
import React from 'react';
import { Skeleton, SkeletonVariants } from '@cx/ui';
import OptionCheckbox from 'core-ui/optionCheckbox/optionCheckbox';
import DataValidationTip from 'components/DataValidation/DataValidationTip';
import { appAssets } from 'constants/assets';
import './FieldsManagerHeader.scss';
import { useStore } from 'store/useGlobalStore';

const FilterIcon = appAssets.icons.FILTERNOTAPPLIED;
const SearchIcon = appAssets.icons.SEARCH;

interface FieldsManagerHeaderProps {
  isLoading: boolean;
  dataSourceDisplayName: string;
  fieldCountText: string;
  /** Text describing how many partition filters are currently applied. */
  filterCountText: string;
  /** True when at least one field on this partition is incomplete. */
  hasInvalidFields: boolean;
  /** True when at least one partition-level filter is incomplete. */
  hasInvalidPartitionFilters: boolean;
  /** True when the fields count reaches the search threshold. */
  isSearchMode: boolean;
  searchTerm: string;
  areAllFieldsExpanded: boolean;
  onAddField: () => void;
  onSearchTermChange: (value: string) => void;
  onToggleExpandAll: (isExpanded: boolean) => void;
  onManagePartitionFilters?: () => void;
}

/**
 * Presentational header of the FieldsManager view.
 * Renders the partition filters entry point, the "Add field" action and,
 * depending on the fields count, either the "Expand fields" toggle or a search box.
 */
const FieldsManagerHeader: React.FC<FieldsManagerHeaderProps> = ({
  isLoading,
  dataSourceDisplayName,
  fieldCountText,
  filterCountText,
  hasInvalidFields,
  hasInvalidPartitionFilters,
  isSearchMode,
  searchTerm,
  areAllFieldsExpanded,
  onAddField,
  onSearchTermChange,
  onToggleExpandAll,
  onManagePartitionFilters,
}) => {
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    onSearchTermChange(event.target.value);

  const handleExpandAllChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    onToggleExpandAll(event.target.checked);

  const handleManagePartitionFilters = () => onManagePartitionFilters?.();

  const { useDiyMetricLevelFilters } = useStore((state) => ({
    useDiyMetricLevelFilters: state.configDashboard.useDiyMetricLevelFilters,
  }));

  const renderHeaderInfo = () => {
    if (isLoading) {
      return (
        <div className="fields-manager-header-skeleton">
          <Skeleton variant={SkeletonVariants.text} width="140px" />
          <Skeleton variant={SkeletonVariants.text} width="60px" />
        </div>
      );
    }

    return (
      <>
        <div className="dataSourceLabel dataSourcePartitionLabel">
          <span>{dataSourceDisplayName}</span>
        </div>
        <div className="dataSourceFieldsLabel">
          <span>{fieldCountText}</span>
          <span className="dataSourceFieldsLabel__validation" aria-hidden={!hasInvalidFields}>
            {hasInvalidFields && (
              <DataValidationTip
                message="Invalid fields are present on this partition. They will be automatically removed if you go back."
                kind="error"
                placement="bottom"
              />
            )}
          </span>
        </div>
      </>
    );
  };

  const renderPartitionFilters = () => {
    if (isLoading) {
      return (
        <div className="fields-manager-header__partition-filters-wrapper">
          <Skeleton
            variant={SkeletonVariants.rectangular}
            width="110px"
            height="20px"
            className="fields-manager-header__skeleton fields-manager-header__skeleton--filters"
          />
          <Skeleton variant={SkeletonVariants.text} width="60px" />
        </div>
      );
    }

    return (
      <div className="fields-manager-header__partition-filters-wrapper">
        <button
          type="button"
          className="fields-manager-header__partition-filters"
          onClick={handleManagePartitionFilters}
          title="Project filter"
          aria-label="Project filter"
        >
          <FilterIcon />
        </button>
        <div className="dataSourceFiltersLabel">
          <span>{filterCountText}</span>
          {hasInvalidPartitionFilters && (
            <span className="dataSourceFiltersLabel__validation">
              <DataValidationTip
                message="Invalid project filters are present."
                kind="error"
                placement="bottom"
              />
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderAddField = () => {
    if (isLoading) {
      return (
        <Skeleton
          variant={SkeletonVariants.rectangular}
          width="100px"
          height="35px"
          className="fields-manager-header__skeleton fields-manager-header__skeleton--add"
        />
      );
    }

    return (
      <button className="add-field-button" onClick={onAddField}>
        + Add field
      </button>
    );
  };

  const renderSecondaryControl = () => {
    if (isLoading) {
      return (
        <Skeleton
          variant={SkeletonVariants.rectangular}
          width="120px"
          height="22px"
          className="fields-manager-header__skeleton fields-manager-header__skeleton--secondary"
        />
      );
    }

    if (isSearchMode) {
      return (
        <div className="fields-manager-header__search-wrapper">
          <input
            type="text"
            className="fields-manager-header__search"
            placeholder="Search fields"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <SearchIcon className="fields-manager-header__search-icon" />
        </div>
      );
    }

    return (
      <div className="fields-manager-header__expand-toggle">
        <OptionCheckbox
          key={`expand-fields-${areAllFieldsExpanded}`}
          option={{
            displayName: 'Expand fields',
            actionName: 'expand-fields',
            isChecked: areAllFieldsExpanded,
          }}
          onChangeHandler={handleExpandAllChange}
          size="small"
          labelPosition="right"
        />
      </div>
    );
  };

  return (
    <div className="fields-manager-header">
      <div className="fields-manager-header__top">
        <div className="data-source-info">{renderHeaderInfo()}</div>
        {useDiyMetricLevelFilters && renderPartitionFilters()}
      </div>

      <div className="fields-manager-header__bottom">
        {renderAddField()}
        {renderSecondaryControl()}
      </div>
    </div>
  );
};

export default FieldsManagerHeader;
